import * as React from 'react'
import { accentTheme, type AccentSurface } from '@lib/helpers/accent-theme'
import type { HexColor } from '@lib/validators/tenant.validator'

const ACCENT_VARIABLE_NAMES = [
  '--primary',
  '--primary-hover',
  '--primary-foreground',
  '--ds-fg-link',
  '--ring',
] as const

type AccentVariableName = (typeof ACCENT_VARIABLE_NAMES)[number]

/** CSS variables set for an organization's accent. Absent keys keep PixelSign's. */
type AccentVariables = Partial<Record<AccentVariableName, string>>

/**
 * The CSS variables a signer page sets for an organization's accent (see
 * accentTheme): primary buttons, their hover and text, and, where the color
 * reads well on the page's `surface`, links and focus rings. Empty to keep
 * PixelSign's. The attention variables (signing fields) are never touched.
 */
export function accentVariables(
  accentColor: HexColor | null | undefined,
  surface: AccentSurface = 'light',
): AccentVariables {
  const theme = accentTheme(accentColor, surface)
  if (!theme) return {}
  return {
    '--primary': theme.accent,
    '--primary-hover': theme.hover,
    '--primary-foreground': theme.foreground === 'light' ? 'var(--ds-fg-on-accent)' : 'var(--ds-fg-on-light-fill)',
    ...(theme.links ? { '--ds-fg-link': theme.accent } : {}),
    ...(theme.ring ? { '--ring': theme.accent } : {}),
  }
}

/**
 * Themes the document with the organization's accent while a signer page is
 * open. It's set on <html> rather than the page, so dialogs and menus portaled
 * to <body> (adopt signature, decline) match, and cleared when the page goes.
 */
export function useAccentTheme(accentColor: HexColor | null | undefined, surface: AccentSurface = 'light'): void {
  React.useEffect(() => {
    const variables = accentVariables(accentColor, surface)
    const style = document.documentElement.style
    const applied: AccentVariableName[] = []
    for (const name of ACCENT_VARIABLE_NAMES) {
      const value = variables[name]
      if (value === undefined) {
        continue
      }
      style.setProperty(name, value)
      applied.push(name)
    }
    return () => {
      for (const name of applied) style.removeProperty(name)
    }
  }, [accentColor, surface])
}
